import { useEffect, useState } from "preact/hooks";
import { activeSolution, ec2Cache, solutionIndex, solutionInput } from "../state/appState";
import { fetchEc2Instances } from "../api/ec2Client";
import { navigateTo } from "../router";
import { webClientUrl } from "../config";
import type { Environment } from "../types";
import { Spinner } from "./Spinner";

export function SolutionSearch() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickerSolution, setPickerSolution] = useState("");
  const [pickerEnv, setPickerEnv] = useState<Environment | "">("");

  async function loadAllSolutions() {
    setLoading(true);
    setError(null);
    try {
      ec2Cache.value = await fetchEc2Instances("all");
    } catch (err) {
      console.error("Error fetching solutions:", err);
      setError("Error fetching solutions. Refresh page.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAllSolutions();

    const params = new URLSearchParams(window.location.search);
    const solutionParam = params.get("solution");
    if (solutionParam && solutionParam.length > 1) {
      solutionInput.value = solutionParam;
      activeSolution.value = solutionParam;
      navigateTo("demand");
    }
  }, []);

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      const value = solutionInput.value.trim();
      if (value) {
        activeSolution.value = value;
      }
    }
  }

  function handlePickerSubmit(event: Event) {
    event.preventDefault();
    if (!pickerSolution || !pickerEnv) return;
    window.open(webClientUrl(pickerEnv, pickerSolution), "_blank");
  }

  const sortedSolutions = [...solutionIndex.value.keys()].sort();
  const pickerEnvOptions = pickerSolution ? [...(solutionIndex.value.get(pickerSolution) ?? [])] : [];

  return (
    <div id="solution-search">
      <div class="search-primary-row">
        <select
          value={pickerSolution}
          onChange={(e) => {
            const value = (e.target as HTMLSelectElement).value;
            setPickerSolution(value);
            setPickerEnv("");
            solutionInput.value = "";
            activeSolution.value = value;
          }}
        >
          <option value="">Select a solution</option>
          {sortedSolutions.map((sol) => (
            <option key={sol} value={sol}>
              {sol}
            </option>
          ))}
        </select>
      </div>

      <form id="open-instance-form" onSubmit={handlePickerSubmit}>
        <select
          value={pickerEnv}
          onChange={(e) => setPickerEnv((e.target as HTMLSelectElement).value as Environment)}
          disabled={!pickerSolution}
        >
          <option value="">Environment</option>
          {pickerEnvOptions.map((env) => (
            <option key={env} value={env}>
              {env}
            </option>
          ))}
        </select>
        <button type="submit" disabled={!pickerSolution || !pickerEnv}>
          Open Instance
        </button>
      </form>

      <div class="search-secondary-row">
        <label class="search-label" for="solution">or search:</label>
        <input
          id="solution"
          type="text"
          value={solutionInput.value}
          onInput={(e) => (solutionInput.value = (e.target as HTMLInputElement).value)}
          onKeyDown={handleKeyDown}
          placeholder="e.g. Danhost*|NTT-Uni — press Enter"
        />
      </div>

      <div class="search-status-row">
        <span id="response">
          {!loading && !error && `Found ${solutionIndex.value.size} solutions.`}
          {error}
          {" "}
          {loading
            ? <Spinner />
            : <span class="refresh-cache" title="Refresh/clear cache" onClick={loadAllSolutions}>🔄</span>
          }
        </span>
      </div>
    </div>
  );
}
